<svelte:options />

<script>
    /* global IntersectionObserver */
    import { run } from 'svelte/legacy';
    import { onDestroy, onMount, createEventDispatcher, getContext } from 'svelte';
    import { collection, doc, getDocs } from 'firebase/firestore';
    import { fade } from 'svelte/transition';
    import { goto } from '$app/navigation';
    import { page } from '$app/state';
    import BookAccordianSection from './BookAccordianSection.svelte';
    import './book-accordion.css';
    import { lastDocumentViewerItem } from '@repo/stores/Actions';
    import ResourceHub from './resource/ResourceHub.svelte';
    import VisibilityBadge from './VisibilityBadge.svelte';
    import { CONTENT_VISIBILITY_CONTEXT } from './contentVisibilityContext.js';
    import { currentBreadcrumb } from '@repo/stores/NavigationStore';
    import { resolveStorageUrl } from '@repo/config/Storage';
    import { postHogCapture } from '@repo/config/postHogHelper';
    import { getBookAccordionDeps } from './context.js';

    const { access, db, features } = getBookAccordionDeps();
    const { canAccessTeacherOnlyMaterial, canAccessResource, hasBookAccess = () => false } = access;
    const isContentVisibilityEnabled = features?.isContentVisibilityEnabled ?? (() => false);
    const { getFirestore, contentDb } = db;

    const visibilityCtx = getContext(CONTENT_VISIBILITY_CONTEXT);
    
    const subItemCache = new Map();
    /**
     * @typedef {Object} Props
     * @property {any} data
     * @property {any} activeItem
     * @property {any} [level]
     * @property {Map<string, Set<string>>} [hiddenIdsByCollection]
     * @property {Set<string> | null} [currentHiddenIds]
     * @property {Set<string>} [hiddenTitlesSet]
     * @property {boolean} [parentHidden]
     */
    let {
        data,
        activeItem,
        level = null,
        hiddenIdsByCollection = new Map(),
        currentHiddenIds = null,
        hiddenTitlesSet = new Set(),
        parentHidden = false,
    } = $props();

    let resolvedHiddenIds = $derived(
        data?.catalog === 'Title' && hiddenIdsByCollection?.get
            ? (hiddenIdsByCollection.get(data.id) ?? currentHiddenIds)
            : currentHiddenIds
    );

    // VisibilityBadge handles its own auth gate (hides for non-teachers).
    // Title rows match against hiddenTitlesSet, child rows match against
    // the parent title's currentHiddenIds.
    let isHiddenFromStudents = $derived.by(() => {
        if (!data) return false;
        if (data.catalog === 'Title') return hiddenTitlesSet?.has?.(data.id) === true;
        return currentHiddenIds?.has?.(data.id) === true;
    });

    let visibilityPending = $derived(
        !!data?.id && visibilityCtx?.pendingIds?.has?.(data.id) === true
    );

    // A row is effectively hidden when it's flagged itself OR sits anywhere
    // under a hidden ancestor — hiding a parent hides the whole subtree.
    let effectiveHidden = $derived(isHiddenFromStudents || parentHidden === true);

    // Teachers keep hidden rows in the tree but see them dimmed/disabled;
    // students never render hidden rows at all, so the guard is belt-and-braces.
    let showAsHidden = $derived(effectiveHidden && isContentVisibilityEnabled() && canAccessTeacherOnlyMaterial());

    /**
     * Pull the parent title id out of the row's docPath. Top-level book
     * docs sit at `read-only-titles/{titleId}`, descendants at
     * `read-only-titles/{titleId}/items/.../items/{leaf}` — the second
     * segment is always the title id.
     */
    function resolveTitleId() {
        if (data?.catalog === 'Title') return data.id;
        const parts = (data?.docPath || '').split('/');
        return parts[1] || null;
    }

    function handleVisibilityToggle() {
        if (!visibilityCtx || !data) return;
        if (data.catalog === 'Title') {
            visibilityCtx.toggleTitle(data.id, isHiddenFromStudents);
            return;
        }
        const titleId = resolveTitleId();
        if (!titleId) return;
        visibilityCtx.toggleItem({
            collectionId: titleId,
            itemId: data.id,
            docPath: data.docPath || null,
            currentlyHidden: effectiveHidden,
        });
    }

    const dispatch = createEventDispatcher();

    let showSubItems = $state(false);
    let subItems = $state([]);
    let isLoadingChildren = $state(false);
    let hasLoadedChildren = $state(false);
    let lastActiveItem = $state(0);
    let titles = $state({});
    let hasIntersected = $state(false);
    let itemElement = $state(null);
    let intersectionObserver = null;

    run(() => {
        if (activeItem) {
            if (activeItem !== data.id && data?.catalog?.toLowerCase() !== 'divider') {
                showSubItems = false;
            }
        }
    });

    let hasAccess = $state(false);
    let lastDocumentUnsub = null;
    let breadcrumbUnsub = null;
    let breadcrumbTimer = null;
    let loadError = $state(null);
    let fetchVersion = 0;
    let isDivider = $state(false);

    async function getSubItems() {
        if (!data?.docPath) return;
        const requestVersion = ++fetchVersion;
        isLoadingChildren = true;
        loadError = null;
        subItems = [];

        const canAccessTeacherOnlyContent = canAccessTeacherOnlyMaterial();
        const hiddenSig = !canAccessTeacherOnlyContent && resolvedHiddenIds && resolvedHiddenIds.size
            ? Array.from(resolvedHiddenIds).sort().join(',')
            : '';
        const cacheKey = `${data.docPath}|${canAccessTeacherOnlyContent ? 'teacher' : 'student'}|${hiddenSig}`;
        const TTL_MS = 5 * 60 * 1000;

        const cached = subItemCache.get(cacheKey);
        if (cached && cached.expiresAt > Date.now()) {
            subItems = cached.items;
            hasAccess = cached.hasAccess;
            hasLoadedChildren = true;
            isLoadingChildren = false;
            return;
        }

        try {
            const fetching = (async () => {
                const bookId = data._collectionId || data.id;
                const quickAccess = hasBookAccess(bookId);
                // `docPath` always points at `read-only-titles/...` which
                // lives in content-db. Fall back to getFirestore() only if
                // the host app hasn't wired contentDb yet.
                const dbRef = contentDb || getFirestore();
                const pathSegments = data.docPath.split('/');
                const docRef = doc(dbRef, ...pathSegments);
                const childrenSnap = await getDocs(collection(docRef, 'items'));

                const fetchedItems = childrenSnap.docs.map(child => {
                    const childData = child.data();
                    childData.id = child.id;
                    childData.docPath = child.ref.path;
                    childData._collectionId = childData._collectionId || bookId;
                    return childData;
                }).filter(childData => {
                    const intendedEndUserRole = childData.intendedEndUserRole || null;
                    return intendedEndUserRole !== 'Teacher' || canAccessTeacherOnlyContent;
                }).filter(childData => {
                    if (canAccessTeacherOnlyContent) return true;
                    if (!resolvedHiddenIds || resolvedHiddenIds.size === 0) return true;
                    return !resolvedHiddenIds.has(childData.id);
                }).sort((a, b) => {
                    const aOrder = a._order ?? a._index;
                    const bOrder = b._order ?? b._index;

                    if (aOrder !== undefined && bOrder !== undefined && aOrder !== bOrder) {
                        return aOrder - bOrder;
                    }
                    const aTitle = (a.title || '').toLowerCase();
                    const bTitle = (b.title || '').toLowerCase();
                    if (aTitle < bTitle) return -1;
                    if (aTitle > bTitle) return 1;
                    return 0;
                });

                let finalAccess = quickAccess;
                if (!finalAccess && !data._collectionId) {
                    finalAccess = await canAccessResource(data.id);
                }
                return { items: fetchedItems, hasAccess: finalAccess };
            })();

            subItemCache.set(cacheKey, fetching);
            const { items, hasAccess: access } = await fetching;
            if (requestVersion !== fetchVersion) return;
            subItems = items;
            hasAccess = access;
            subItemCache.set(cacheKey, { items, hasAccess: access, expiresAt: Date.now() + TTL_MS });
        } catch (error) {
            console.error(error);
            if (requestVersion !== fetchVersion) return;
            loadError = 'Unable to load items. Please try again.';
            subItemCache.delete(cacheKey);
            hasLoadedChildren = false;
        } finally {
            if (requestVersion === fetchVersion) {
                hasLoadedChildren = !loadError;
                isLoadingChildren = false;
            }
        }
    }

    lastDocumentUnsub = lastDocumentViewerItem.subscribe((i) => {
        if (i && data && i.id === data.id) {
            lastActiveItem = i.id;
            if (document.getElementById('el_' + i.id))
                document
                    .getElementById('el_' + i.id)
                    .scrollIntoView({ behavior: 'smooth' });
            showSubItems = true;
        }
    });

    function toggle() {
        if (isDivider) return;
        showSubItems = !showSubItems;
        if (showSubItems) open();

        dispatch('toggle', {
            itemId: data.id,
        });

        postHogCapture('library_accordian_toggle_item', { itemId: data.id });
    }

    async function open() {
        showSubItems = true;
        if (data?._collection === 'resources') {
            hasLoadedChildren = true;
            isLoadingChildren = false;
            return;
        }
        if (!hasLoadedChildren) {
            await getSubItems();
        }
    }

    function formatSortNumber(m) {
        return m.padStart(4, '0');
    }

    function handleDividerClick() {
        if (!data?.location) return;

        const { params } = page;
        const { classId } = params;

        if (classId) {
            goto(`/classroom/${classId}/teach/${data.id}`);
        }
    }

    function onHeaderClick() {
        if (isDivider) {
            if (data?.location) handleDividerClick();
            return;
        }
        toggle();
    }

    function retryLoad() {
        getSubItems();
    }

    function parseTitle(title, type) {
        const t = { main: title, aside: null };

         
        const ASReg = new RegExp(
            /([0-9]){1,2}: ([AS ]{3})([0-9]{1,}).([0-9]{1,})(.{1,})+/
        );

        switch (type) {
            case 'Unit':
                if (/^[0-9]{1,3}/.test(title)) { // if it starts with numbers
                    t.aside = 'Unit ' + title.split(' ')[0];
                    t.main = title.split(' ').slice(1).join(' ');
                } else if (title.match(ASReg) && title.match(ASReg).length > 0) {
                    const ma = title.match(ASReg);
                    t.aside = `chapter ${ma[1]}`;
                    t.main = ma[5].trim();
                } else if (/^chapter [0-9]{1,3}( |: | : )/gi.test(title)) {
                    t.aside = title.match(/chapter [0-9]{1,3}/gi)[0];
                    t.main = title.replace(/chapter [0-9]{1,3}( |: | : )/gi, '');
                }
                break;
            case 'Activity':
                if (/^[0-9]{1,3}/.test(title)) { // if it starts with numbers
                    t.aside = 'Activity ' + title.split(' ')[0];
                    t.main = title.split(' ').slice(1).join(' ');
                } else if (title.match(ASReg) && title.match(ASReg).length > 0) {
                    const ma = title.match(ASReg);
                    t.aside = 'introduction';
                    t.main = ma[5].trim();
                } else if (/chapter [0-9]{1,3}( |: | : )/gi.test(title)) {
                    t.aside = 'introduction';
                    t.main = title.replace(/chapter [0-9]{1,3}( |: | : )/gi, '');
                } else if (/^introduction [0-9]{1,3}( |: | : )/gi.test(title)) {
                    t.aside = 'introduction';
                    t.main = title.replace(/^introduction [0-9]{1,3}( |: | : )/gi, '');
                }
                break;
            case 'Folder': {
                const folderSplit = title.split(':');
                t.aside = folderSplit[0];
                t.main = folderSplit[1];
                break;
            }
        }
        return t;
    }

    function getDataTitle(objData) {
        if (objData?._order !== undefined) return objData._order.toString().padStart(5, '0') + ' ' + (objData.title || '');
        if (objData?._index !== undefined) return objData._index.toString().padStart(5, '0') + ' ' + (objData.title || '');
        if (/order:/gi.test(objData.keywords))
            return (
                objData.keywords
                    .match(/order:[0-9]{1,3}/gi)[0]
                    .split(':')[1]
                    .padStart(5, '0') +
                ' ' +
                objData.title
            );
        if (
            /^introduction/gi.test(objData.title) ||
            /introduction/gi.test(objData.keywords) ||
            /^book introduction/gi.test(objData.title)
        )
            return '00000 ' + objData.title;
        if (/chapter [0-9]{1,3}( |: | : )/gi.test(objData.title))
            return (
                objData.title.match(/[0-9]{1,3}/g)[0].padStart(4, '0') +
                ' ' +
                objData.title
            );
        if (/appendix/gi.test(objData.title)) return 'ZZZZ ' + objData.title;
        return objData.title.replace(/\b\d{1,}\b/, formatSortNumber);
    }

    onMount(() => {
        if (data?.catalog?.toLowerCase() === 'divider') {
            showSubItems = true;
            open();
        }

        breadcrumbTimer = setTimeout(() => {
            breadcrumbUnsub = currentBreadcrumb.subscribe((bc) => {
                if (bc?.breadcrumb && bc.breadcrumb.length > 0) {
                    bc.breadcrumb.forEach((b) => {
                        if (
                            b.id === data.id &&
                            document.getElementById('el_' + data.id)
                        )
                            document
                                .getElementById('el_' + data.id)
                                .scrollIntoView({ behavior: 'smooth' });
                        if (b.id === data.id) open();
                    });
                }
            });
        }, 500);

        if (!itemElement || typeof IntersectionObserver === 'undefined') {
            hasIntersected = true;
            return;
        }

        intersectionObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    hasIntersected = true;
                    intersectionObserver?.disconnect();
                }
            });
        }, { rootMargin: '200px' });

        intersectionObserver.observe(itemElement);
    });

    $effect(() => {
        if (data?._collection === 'resources') {
            hasLoadedChildren = true;
            isLoadingChildren = false;
        }

        isDivider = data?.catalog?.toLowerCase() === 'divider';
        titles = parseTitle(data?.title || '', data?.catalog);
    });

    onDestroy(() => {
        if (breadcrumbTimer) clearTimeout(breadcrumbTimer);
        if (breadcrumbUnsub) breadcrumbUnsub();
        if (lastDocumentUnsub) lastDocumentUnsub();
        if (intersectionObserver) intersectionObserver.disconnect();
    });

    export {
        data,
        activeItem,
        level,
    };
</script>

{#if data}
    {@const shouldBeHeading = !!(level && level >= 1 && level <= 6)}

    <li
        id={'el_' + data.id}
        bind:this={itemElement}
        transition:fade
        class="collection-item book-accordian-item {showAsHidden ? 'hidden-from-students' : ''}"
        data-id={data.id}
        data-title={getDataTitle(data)}
    >
        <svelte:element
            this={shouldBeHeading ? `h${level}` : 'button'}
            id={'el_btn' + data.id}
            onclick={() => (shouldBeHeading ? null : onHeaderClick())}
            class="collection-header interactive"
            style:background-color={`#${(data.hexCodeBackground || '').replaceAll('#', '')}`}
            aria-expanded={shouldBeHeading ? null : !!showSubItems}
            aria-controls={shouldBeHeading ? null : 'el_col_bd_' + data.id}
            aria-busy={isLoadingChildren}
        >
            <svelte:element
                this={shouldBeHeading ? 'button' : 'div'}
                onclick={() => (shouldBeHeading ? onHeaderClick() : null)}
                aria-expanded={shouldBeHeading && !isDivider ? !!showSubItems : null}
                aria-controls={shouldBeHeading ? 'el_col_bd_' + data.id : null}
                aria-busy={isLoadingChildren}
                class="collection-header-inner {lastActiveItem === data.id
                    ? 'on'
                    : ''} {data.catalog?.toLowerCase()} {showSubItems
                        ? 'open'
                        : ''} interactive"
            >
                {#key titles}
                    {#if data.thumbnailURI && data.catalog === 'Title'}
                        {#await resolveStorageUrl(data.thumbnailURI) then resolvedSrc}
                            <img
                                class="icon {data.catalog.toLowerCase()}"
                                src={resolvedSrc}
                                alt=""
                            />
                        {/await}
                    {/if}
                    {#if titles.aside && (!('hasBadge' in data) || data?.hasBadge === true)}
                        <span
                            class={data.catalog.toLowerCase() + ' aside'}
                            style:background-color={`#${(data.hexCodeBadgeBackground || '').replaceAll('#', '')}`}
                            style:color={`#${(data.hexCodeBadgeText || '').replaceAll('#', '')}`}
                            data-originaltitle={data.title}>{titles.aside}</span>
                    {/if}
                    {#if titles.main}
                        <span
                            style={data?.hexCodeText ? 'color: #' + data.hexCodeText.replaceAll('#', '') : ''}
                        >{titles.main}</span>
                    {/if}
                    <div class="spacer"></div>
                    <span class="row-visibility-slot">
                        <VisibilityBadge
                            hidden={effectiveHidden}
                            onclick={visibilityCtx ? handleVisibilityToggle : null}
                            pending={visibilityPending}
                        />
                    </span>
                    {#if data.catalog === 'Title' || data.catalog === 'Unit' || data.catalog === 'Activity' || data.catalog === 'Folder'}
                        <i
                            class="material-icons"
                            aria-hidden="true"
                            style={data?.overrideArrowHexCode ? 'color: #' + data.overrideArrowHexCode.replaceAll('#', '') : (data?.hexCodeBadgeBackground ? 'color: #' + data.hexCodeBadgeBackground.replaceAll('#', '') : '')}
                        >
                            {showSubItems ? 'expand_less' : 'expand_more'}
                        </i>
                    {/if}
                {/key}
            </svelte:element>
        </svelte:element>

        <div
            id={'el_col_bd_' + data.id}
            class="collection-body"
            style={showSubItems ? '' : 'display:none;'}
        >
            {#if hasIntersected}
                {#if loadError}
                    <div class="loader error" role="alert">
                        <span>{loadError}</span>
                        <button class="retry-btn interactive" onclick={retryLoad} aria-busy={isLoadingChildren}>Retry</button>
                    </div>
                {:else if subItems?.length > 0}
                    {#if (data.catalog === 'Activity' || data.keywords?.indexOf('Activity Page') > -1)}
                        <ResourceHub items={subItems} disabled={!hasAccess} level={level} hiddenIds={resolvedHiddenIds} parentHidden={effectiveHidden} />
                    {:else}
                        <BookAccordianSection
                            items={subItems}
                            parentID={data.id}
                            level={level !== null ? level + 1 : null}
                            {hiddenIdsByCollection}
                            currentHiddenIds={resolvedHiddenIds}
                            {hiddenTitlesSet}
                            parentHidden={effectiveHidden}
                        />
                    {/if}
                {:else if isLoadingChildren}
                    <div class="loader">
                        <img src="/ui/loader.svg" alt="Loading icon" />
                    </div>
                {/if}
            {/if}
        </div>
    </li>
{/if}
